import React, { useState, useEffect, useCallback } from 'react';
import { Branch, Tentor, UserRole } from '../../types/db';
import {
  Plus,
  Trash2,
  Loader2,
  Save,
  X,
  UserCheck,
  Mail,
  Phone,
  Building2,
  BookOpen,
  Sparkles,
  Lock,
  DollarSign,
  User,
  Edit3,
  KeyRound,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export interface TeacherManagerProps {
  branchId?: string;
}

export const TeacherManager: React.FC<TeacherManagerProps> = ({ branchId }) => {
  const [tentors, setTentors] = useState<Tentor[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // State untuk mode edit
  const [editingTentor, setEditingTentor] = useState<Tentor | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    base_salary: 100000,
    phone: '',
    branch_id: branchId && branchId !== 'ALL' ? branchId : '',
  });
  const [password, setPassword] = useState('');

  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      let query = supabase.from('tentors').select('*');
      if (branchId && branchId !== 'ALL') {
        query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw error;

      setTentors(
        data.map((item) => ({
          ...item,
          name: item.name || 'Unknown',
          email: item.email || '-',
        }))
      );

      const { data: bData } = await supabase.from('branches').select('*');
      if (bData) setBranches(bData.filter((b) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));
    } catch (err: any) {
      /* console removed */
    } finally {
      setFetching(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenAdd = () => {
    setEditingTentor(null);
    setFormData({
      name: '',
      subject: '',
      base_salary: 100000,
      phone: '',
      branch_id: branchId && branchId !== 'ALL' ? branchId : '',
    });
    setPassword('');
    setIsFormOpen(true);
  };

  const handleOpenEdit = (t: Tentor) => {
    setEditingTentor(t);
    setFormData({
      name: t.name || '',
      subject: t.subject || '',
      base_salary: t.base_salary || 100000,
      phone: '',
      branch_id: t.branch_id || '',
    });
    setPassword('');
    setIsFormOpen(true);
  };

  // Aktivasi/Buatkan Akun Login untuk Tentor yang Belum Ada Auth-nya
  const handleCreateAuthForExisting = async (t: Tentor) => {
    const phone = window.prompt(`Masukkan No. WhatsApp untuk ID Login ${t.name || 'Tentor'}:`);
    if (!phone) return;

    const pass = window.prompt(`Set Password Login untuk ${t.name || 'Tentor'}:`);
    if (!pass) return;

    const email = `${phone.replace(/\D/g, '')}@delta.com`;

    setFetching(true);
    try {
      const { error } = await supabase.functions.invoke('create-admin-user', {
        body: {
          email,
          password: pass,
          name: (t.name || 'TENTOR').toUpperCase(),
          branch_id: t.branch_id || null,
          subject: t.subject || 'UMUM',
          base_salary: t.base_salary || 100000,
          role: UserRole.TENTOR,
        },
      });

      if (error) throw error;
      window.alert(`AKUN BERHASIL DIAKTIFKAN!\n\nEmail Login: ${email}\nPassword: ${pass}`);
      loadData();
    } catch (err: any) {
      window.alert('Gagal membuat akun: ' + err.message);
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.subject) {
      return window.alert('MOHON LENGKAPI DATA: Nama dan Mapel wajib diisi!');
    }

    setLoading(true);
    try {
      if (editingTentor) {
        await supabase.functions.invoke('update-user', {
          body: {
            user_id: editingTentor.user_id,
            name: formData.name.toUpperCase(),
            branch_id: formData.branch_id || null,
          },
        });

        const { error: tentorErr } = await supabase
          .from('tentors')
          .update({
            name: formData.name.toUpperCase(),
            subject: formData.subject.toUpperCase(),
            base_salary: Number(formData.base_salary),
            branch_id: formData.branch_id || null,
          })
          .eq('user_id', editingTentor.user_id);

        if (tentorErr) throw tentorErr;
        
        window.alert('Data pengajar berhasil diperbarui!');
      } else {
        if (!formData.phone || !password) {
          throw new Error("WhatsApp dan Password wajib diisi untuk tentor baru!");
        }
        
        const email = `${formData.phone.replace(/\D/g, '')}@delta.com`;

        const { error } = await supabase.functions.invoke('create-admin-user', {
          body: {
            email,
            password: password,
            name: formData.name.toUpperCase(),
            branch_id: formData.branch_id || null,
            subject: formData.subject.toUpperCase(),
            base_salary: Number(formData.base_salary),
            role: UserRole.TENTOR,
          },
        });

        if (error) throw error;
        window.alert(
          `BERHASIL!\nTentor: ${formData.name} telah diaktifkan.\nID Login: ${email}\nPassword: ${password}`
        );
      }

      setIsFormOpen(false);
      await loadData();
    } catch (err: any) {
      window.alert('Gagal Memproses Data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Hapus akses login & data pengajar ini? Tindakan ini permanen.')) return;
    try {
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { user_id: userId },
      });
      if (error) throw error;
      
      await loadData();
      window.alert('Data pengajar berhasil dihapus.');
    } catch (err: any) {
      window.alert('Gagal Menghapus: ' + err.message);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
            <UserCheck className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-none">
              Database Pengajar
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
              Daftar Tentor Terintegrasi Seluruh Jaringan
            </p>
          </div>
        </div>
        <button
          onClick={handleOpenAdd}
          className="bg-indigo-600 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase shadow-xl hover:bg-indigo-700 transition-all flex items-center gap-2 hover:-translate-y-0.5 active:scale-95"
        >
          <Plus className="w-4 h-4" /> Tambah Tentor Baru
        </button>
      </div>

      {fetching ? (
        <div className="py-32 flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin text-indigo-600 w-12 h-12" />
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.4em]">
            Sinkronisasi Data...
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tentors.map((t) => (
            <div
              key={t.id}
              className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm group hover:shadow-xl transition-all relative overflow-hidden flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start mb-6">
                  <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center font-black text-xl uppercase shadow-inner group-hover:bg-indigo-600 group-hover:text-white transition-all">
                    {t.name?.charAt(0) || 'T'}
                  </div>
                  <div className="flex gap-1">
                    {!t.user_id && (
                      <button
                        onClick={() => handleCreateAuthForExisting(t)}
                        className="text-amber-500 hover:text-amber-600 p-2 transition-colors hover:bg-amber-50 rounded-lg"
                        title="Buatkan Akun Login"
                      >
                        <KeyRound className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEdit(t)}
                      className="text-slate-300 hover:text-indigo-600 p-2 transition-colors hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(t.user_id)}
                      className="text-slate-300 hover:text-rose-500 p-2 transition-colors hover:bg-rose-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <h3 className="font-black text-base text-slate-900 mb-1 uppercase truncate tracking-tight">
                  {t.name}
                </h3>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Mail className="w-3 h-3 text-indigo-300" /> {t.email}
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-xl text-[8px] font-black uppercase border border-indigo-100">
                    <BookOpen className="w-3 h-3" /> {t.subject}
                  </div>
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[8px] font-black uppercase border ${
                      !t.branch_id
                        ? 'bg-amber-50 text-amber-600 border-amber-100'
                        : 'bg-slate-50 text-slate-500 border-slate-100'
                    }`}
                  >
                    <Building2 className="w-3 h-3" />{' '}
                    {!t.branch_id ? 'GLOBAL / HQ' : `Unit: ${t.branch_id}`}
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 p-5 rounded-[1.5rem] border border-emerald-100 flex justify-between items-center mt-auto">
                <div>
                  <p className="text-[7px] font-black text-emerald-600 uppercase tracking-widest mb-1">
                    Fee Per Sesi
                  </p>
                  <p className="font-black text-sm text-emerald-700">
                    Rp {t.base_salary ? t.base_salary.toLocaleString() : 0}
                  </p>
                </div>
                <Sparkles className="w-5 h-5 text-emerald-300 animate-pulse" />
              </div>
            </div>
          ))}
          {tentors.length === 0 && (
            <div className="col-span-full py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-50 text-slate-200 uppercase font-black tracking-widest text-xs">
              Belum Ada Tentor Terdaftar
            </div>
          )}
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/70 z-[150] flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto no-scrollbar">
          <div className="bg-white rounded-[3.5rem] w-full max-w-4xl p-10 md:p-14 shadow-2xl animate-fade-in-up border border-white relative my-8 max-h-[95vh] overflow-y-auto no-scrollbar">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-10 right-10 text-slate-300 hover:text-slate-900 transition-all hover:bg-slate-50 p-2 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-12">
              <h3 className="font-black text-3xl text-slate-900 uppercase tracking-tighter leading-none">
                {editingTentor ? 'Edit Data Tentor' : 'Registrasi Tentor Baru'}
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-3">
                {editingTentor ? 'Pembaruan Data & Mutasi Cabang' : 'Aktivasi Akun Akademik Cloud'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                    <User className="w-3 h-3" /> Nama Lengkap
                  </label>
                  <input
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-bold bg-white text-slate-900 outline-none focus:border-indigo-500 transition-all text-sm uppercase"
                    placeholder="MISAL: BUDI SANTOSO, S.PD"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                    <BookOpen className="w-3 h-3" /> Mata Pelajaran Spesialis
                  </label>
                  <input
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-bold bg-white text-slate-900 outline-none focus:border-indigo-500 transition-all text-sm uppercase"
                    placeholder="MISAL: MATEMATIKA / FISIKA"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>
                
                {!editingTentor && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                      <Phone className="w-3 h-3" /> No. WhatsApp (ID Login)
                    </label>
                    <input
                      className="w-full border-2 border-slate-200 p-4 rounded-2xl font-bold bg-white text-slate-900 outline-none focus:border-indigo-500 transition-all text-sm"
                      placeholder="08..."
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                    <DollarSign className="w-3 h-3" /> Honor Mengajar per Sesi
                  </label>
                  <input
                    type="number"
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-indigo-900 outline-none focus:border-indigo-500 transition-all text-lg"
                    value={formData.base_salary}
                    onChange={(e) =>
                      setFormData({ ...formData, base_salary: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                    <Building2 className="w-3 h-3" /> Unit Penugasan Utama
                  </label>
                  <select
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all text-xs cursor-pointer"
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                  >
                    <option value="" className="text-slate-400">-- TIDAK TERIKAT CABANG (GLOBAL) --</option>
                    {branches
                      .filter((b) => b.is_active !== false || b.id === formData.branch_id)
                      .map((b) => (
                        <option key={b.id} value={b.id} className="text-slate-900 font-bold">
                          {b.name.toUpperCase()}
                          {b.is_active === false ? ' (NONAKTIF)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
                
                {!editingTentor && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-rose-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                      <Lock className="w-3 h-3" /> Password Akun
                    </label>
                    <input
                      className="w-full border-2 border-rose-200 p-4 rounded-2xl font-bold bg-white text-slate-900 outline-none focus:border-rose-400 transition-all text-sm"
                      placeholder="Buat password login..."
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-12 pt-10 border-t border-slate-100">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-6 rounded-[2.5rem] font-black uppercase tracking-[0.2em] shadow-2xl hover:bg-indigo-700 transition-all flex justify-center items-center gap-4 hover:scale-[1.01] active:scale-95 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Save className="w-6 h-6" />
                )}{' '}
                {editingTentor ? 'SIMPAN PERUBAHAN TENTOR' : 'KONFIRMASI AKTIVASI TENTOR'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
